import React from 'react';
import CountUp from './CountUp';
import { X, Bell } from 'lucide-react';
import { pushSupported, pushPermission, enablePush } from '../utils/push';
import { pulse } from '../utils/celebrate';

export function Card({ children, style, hover, className }) {
  return (
    <div className={className} style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)',
      transition: hover ? 'transform .3s cubic-bezier(.2,.8,.2,1), box-shadow .3s' : undefined,
      ...style
    }}>{children}</div>
  );
}

export function SectionHeader({ title, count, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, minWidth: 0 }}>
        <span style={{ fontFamily: 'var(--serif)', fontWeight: 400, fontSize: 19, color: 'var(--text)' }}>{title}</span>
        {count !== undefined && <span style={{ fontFamily: 'var(--mono)', fontSize: 10, fontWeight: 400, color: 'var(--accent)', letterSpacing: '0.1em' }}>{count}</span>}
      </div>
      {action}
    </div>
  );
}

export function Btn({ children, onClick, variant = 'default', size = 'md', disabled, style: extraStyle, title }) {
  // STR buttons — mono, uppercase, sharp. Primary = bone→obsidian; success = sea of gold.
  const variants = {
    default:   { background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border2)' },
    primary:   { background: 'var(--bg4)',   color: 'var(--text)', border: '1px solid var(--border2)' },
    secondary: { background: 'transparent', color: 'var(--text)',  border: '1px solid var(--border2)' },
    ghost:     { background: 'transparent', color: 'var(--text2)', border: '1px solid transparent' },
    danger:    { background: 'transparent', color: '#C08079',      border: '1px solid rgba(192,128,121,0.4)' },
    success:   { background: 'linear-gradient(100deg, #BFA067, #A98953 58%, #937741)', color: '#0A0A0C', border: '1px solid var(--accent)' },
  };
  const sizes = { sm: { fontSize: 9.5, padding: '7px 12px' }, md: { fontSize: 10.5, padding: '10px 16px' }, lg: { fontSize: 11.5, padding: '12px 22px' } };
  return (
    <button onClick={onClick} disabled={disabled} title={title} className={variant === 'success' ? 'btn-gold' : undefined} style={{
      ...variants[variant], ...sizes[size], borderRadius: 'var(--radius)',
      fontWeight: 400, fontFamily: 'var(--mono)', letterSpacing: '0.1em', textTransform: 'uppercase',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
      whiteSpace: 'nowrap', transition: 'background .18s, opacity .15s, border-color .18s, transform .12s cubic-bezier(.2,.8,.2,1)',
      ...extraStyle
    }}
    onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.85'; }}
    onMouseLeave={e => { e.currentTarget.style.opacity = disabled ? '0.45' : '1'; }}
    >{children}</button>
  );
}

export function Badge({ label, color = 'gray' }) {
  // STR badges — mono, uppercase, hairline outline (no fills)
  const colors = {
    gray:   'var(--text3)', blue: '#8FA3B0', green: '#8FB08A', red: '#C08079',
    orange: '#C79A5B', yellow: '#C79A5B', purple: '#9B8FB0', teal: 'var(--accent)',
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{
      color: c, border: '1px solid ' + c, background: 'transparent', fontFamily: 'var(--mono)',
      fontSize: 8.5, fontWeight: 400, padding: '4px 9px', borderRadius: 'var(--radius)', whiteSpace: 'nowrap',
      letterSpacing: '0.13em', textTransform: 'uppercase'
    }}>{label}</span>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft:     { label: 'Draft',     color: 'gray' },
    generated: { label: 'Generated', color: 'blue' },
    sent:      { label: 'Sent',      color: 'orange' },
    pending:   { label: 'Pending',   color: 'orange' },
    paid:      { label: 'Paid',      color: 'green' },
    unpaid:    { label: 'Unpaid',    color: 'red' },
    overdue:   { label: 'Overdue',   color: 'red' },
  };
  const m = map[status] || map.draft;
  return <Badge label={m.label} color={m.color} />;
}

export function Modal({ open, onClose, title, children, width = 480 }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.62)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 20, backdropFilter: 'blur(4px)'
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: width }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 400, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 6, cursor: 'pointer', color: 'var(--text2)', display: 'flex' }}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <label className="t-label" style={{ display: 'block', marginBottom: 7 }}>{label}</label>
      {children}
      {hint && <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 5 }}>{hint}</p>}
    </div>
  );
}

const inputBase = {
  width: '100%', padding: '11px 13px', fontSize: 14,
  background: 'var(--bg3)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius)', color: 'var(--text)', boxSizing: 'border-box',
  transition: 'border-color 0.18s', fontFamily: 'var(--font)',
};
const focusOn  = e => { e.target.style.borderColor = 'var(--accent)'; };
const focusOff = e => { e.target.style.borderColor = 'var(--border)'; };

export function Input({ value, onChange, placeholder, type = 'text', min, max, step, autoFocus, style: extraStyle }) {
  return (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      min={min} max={max} step={step} autoFocus={autoFocus}
      onFocus={focusOn} onBlur={focusOff}
      style={{ ...inputBase, ...extraStyle }}
    />
  );
}

export function Select({ value, onChange, children, style: extraStyle }) {
  return (
    <select value={value} onChange={onChange} onFocus={focusOn} onBlur={focusOff} style={{ ...inputBase, ...extraStyle }}>
      {children}
    </select>
  );
}

export function ModalRow({ children }) {
  return <div className="grid-half" style={{ gap: 12 }}>{children}</div>;
}

export function Divider() {
  return <div style={{ borderTop: '1px solid var(--border)', margin: '1.25rem 0' }} />;
}

export function EmptyState({ message }) {
  return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>
      <span className="floaty">{message}</span>
    </div>
  );
}

export function Toast({ toast }) {
  if (!toast) return null;
  // STR toast — graphite ground, hairline border, one brass/semantic edge. "Done" reads gold.
  const colors = { success: 'var(--accent)', error: '#C08079', info: 'var(--text2)', warning: '#C79A5B' };
  const edge = colors[toast.type] || colors.success;
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: `2px solid ${edge}`,
      borderRadius: 'var(--radius)', padding: '14px 18px', fontSize: 13,
      fontFamily: 'var(--font)', fontWeight: 400, letterSpacing: '0.01em',
      color: 'var(--text)', boxShadow: 'var(--shadow-pop)',
      animation: 'toastIn 0.2s ease', maxWidth: 360, display: 'flex', alignItems: 'center', gap: 11, overflow: 'hidden',
    }}>
      <span className="toast-mark" style={{ width: 6, height: 6, background: edge, flexShrink: 0, display: 'inline-block' }} />
      {toast.msg}
      <span key={toast.msg} className="toast-line" style={{ background: edge, opacity: 0.55 }} />
    </div>
  );
}

export function StatCard({ label, value, sub, color = 'var(--text)', icon: Icon, iconColor, iconBg, trend, trendUp }) {
  return (
    <Card className="stat-card" style={{ padding: '20px 20px 18px', flex: 1, minWidth: 0 }}>
      <div className="t-label" style={{ marginBottom: 16 }}>{label}</div>
      <div className="stat-value" style={{ color, letterSpacing: '-0.01em', fontFamily: 'var(--serif)', fontWeight: 400 }}><CountUp value={value} /></div>
      {(sub || trend) && (
        <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.04em', marginTop: 12, display: 'flex', alignItems: 'center', gap: 7, color: trend ? (trendUp ? 'var(--green)' : 'var(--red)') : 'var(--text3)' }}>
          {trend && <span>{trendUp ? '▲' : '▼'} {trend}</span>}
          {sub && <span style={{ color: 'var(--text3)' }}>{sub}</span>}
        </div>
      )}
    </Card>
  );
}

export function EnableNotifications({ empId }) {
  const [show, setShow] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!empId || !pushSupported()) return;
    const perm = pushPermission();
    if (perm === 'granted') { enablePush(empId, true); }       // silent re-save of this device
    else if (perm === 'default') { setShow(true); }            // ask once
  }, [empId]);
  if (!show) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '2px solid var(--accent)', borderRadius: 'var(--radius-lg)', padding: '13px 16px', marginBottom: 20 }}>
      <Bell size={17} color="var(--accent)" />
      <span style={{ flex: 1, minWidth: 180, fontSize: 13, color: 'var(--text2)', fontWeight: 400 }}>Turn on notifications to get pinged about task assignments, reviews and changes — even when the app is closed.</span>
      <button disabled={busy} onClick={async () => { setBusy(true); await enablePush(empId); setBusy(false); setShow(false); }} style={{ background: 'var(--accent)', color: '#0A0A0C', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', padding: '8px 16px', fontSize: 10, fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'var(--mono)', opacity: busy ? 0.6 : 1 }}>{busy ? 'Enabling…' : 'Enable'}</button>
      <button onClick={() => setShow(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text3)', fontSize: 10, fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'var(--mono)' }}>Not now</button>
    </div>
  );
}

export function NotificationBell({ items = [], onMarkRead, onClear, onOpen }) {
  const [open, setOpen] = React.useState(false);
  const unread = items.filter(n => !n.read).length;
  // Something new arrived → the bell wiggles (not on first load, only on an increase).
  const bellRef = React.useRef(null);
  const prevUnread = React.useRef(unread);
  React.useEffect(() => {
    if (unread > prevUnread.current) pulse(bellRef.current, 'wiggle');
    prevUnread.current = unread;
  }, [unread]);
  const toggle = () => { const next = !open; setOpen(next); if (next && unread > 0 && onMarkRead) onMarkRead(); };
  const fmtWhen = (iso) => {
    try {
      const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return 'just now';
      if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
      if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={toggle} title="Notifications" style={{ position: 'relative', background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 8, cursor: 'pointer', color: 'var(--text2)', display: 'flex', alignItems: 'center' }}>
        <span ref={bellRef} style={{ display: 'inline-flex', transformOrigin: '50% 10%' }}><Bell size={18} /></span>
        {unread > 0 && <span style={{ position: 'absolute', top: -6, right: -6, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 'var(--radius)', background: 'var(--accent)', color: '#0A0A0C', fontSize: 9.5, fontWeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)' }}>{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 90 }} />
          <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 330, maxWidth: '90vw', maxHeight: 400, overflowY: 'auto', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-pop)', zIndex: 91 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 16px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg2)' }}>
              <span style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 400, color: 'var(--text)' }}>Notifications</span>
              {items.length > 0 && <button onClick={() => { onClear && onClear(); setOpen(false); }} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 9.5, fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>Clear all</button>}
            </div>
            {items.length === 0
              ? <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>You're all caught up.</div>
              : items.map(n => {
                  const actionable = !!(onOpen && n.kind === 'content' && n.taskId);
                  return (
                    <div key={n.id} className="feed-item" onClick={actionable ? () => { setOpen(false); onOpen(n); } : undefined}
                      style={{ display: 'flex', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', background: n.read ? 'transparent' : 'var(--teal-bg)', cursor: actionable ? 'pointer' : 'default' }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: n.read ? 'var(--border2)' : 'var(--accent)', marginTop: 6, flexShrink: 0 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.4 }}>{n.message}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>{fmtWhen(n.createdAt)}{actionable ? ' · Open calendar →' : ''}</div>
                      </div>
                    </div>
                  );
                })
            }
          </div>
        </>
      )}
    </div>
  );
}

export function Table({ headers, children, emptyMessage }) {
  // Inject each column's header onto its cells as data-label, so that on mobile the
  // table can reflow into stacked "Label — value" cards (see .str-table CSS) instead
  // of forcing a horizontal scroll.
  const rows = React.Children.map(children, (tr) => {
    if (!React.isValidElement(tr) || !tr.props) return tr;
    const cells = React.Children.map(tr.props.children, (td, i) =>
      React.isValidElement(td) ? React.cloneElement(td, { 'data-label': headers[i] }) : td
    );
    return React.cloneElement(tr, null, cells);
  });
  return (
    <div className="table-scroll">
      <table className="str-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr>
            {headers.map(h => (
              <th key={h} className="t-label" style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

export function TR({ children, highlight }) {
  return (
    <tr
      style={{ borderBottom: '1px solid var(--border)', background: highlight ? 'rgba(169,137,83,0.10)' : 'transparent', transition: 'background .15s' }}
      onMouseEnter={e => { if (!highlight) e.currentTarget.style.background = 'var(--bg3)'; }}
      onMouseLeave={e => { if (!highlight) e.currentTarget.style.background = 'transparent'; }}
    >
      {children}
    </tr>
  );
}

export function TD({ children, mono, muted, bold, right, style: extraStyle, ...rest }) {
  // `bold` marks the identifier/name column — render it in the Bodoni serif (the brand
  // treatment for names), like "Red Cup Coffee" on the invoice. `...rest` forwards the
  // data-label the Table injects (for the mobile card reflow).
  return (
    <td {...rest} style={{
      padding: '15px 16px', minHeight: 56,
      color: muted ? 'var(--text3)' : bold ? 'var(--text)' : 'var(--text2)',
      fontFamily: mono ? 'var(--mono)' : bold ? 'var(--serif)' : 'var(--font)',
      fontSize: mono ? 12.5 : bold ? 16 : 14,
      fontWeight: 400, textAlign: right ? 'right' : 'left', letterSpacing: bold ? '0.005em' : undefined,
      fontVariantNumeric: mono ? 'tabular-nums' : undefined, ...extraStyle
    }}>{children}</td>
  );
}

// How a day off is paid (owner, 2026-10-12): the employee decides — from the leave
// allowance, or as a salary cut that leaves the allowance untouched. Used by HR (admin)
// and the employee portal; locked once the month's salary is recorded as paid.
export function PayWithToggle({ value, onChange, disabled }) {
  const opt = (v, label) => {
    const on = (value || 'allowance') === v;
    return (
      <button type="button" disabled={disabled} onClick={() => !disabled && !on && onChange(v)}
        style={{ padding: '4px 9px', fontSize: 11, fontFamily: 'var(--mono)', letterSpacing: '0.02em', border: 'none',
          cursor: disabled ? 'default' : 'pointer', borderRadius: 'var(--radius)',
          background: on ? (v === 'salary' ? 'rgba(192,128,121,0.18)' : 'rgba(96,165,250,0.16)') : 'transparent',
          color: on ? (v === 'salary' ? '#C08079' : 'var(--blue)') : 'var(--text3)', opacity: disabled && !on ? 0.5 : 1 }}>
        {label}
      </button>
    );
  };
  return (
    <span style={{ display: 'inline-flex', gap: 2, background: 'var(--bg4)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 2 }}>
      {opt('allowance', 'Allowance')}{opt('salary', 'Salary cut')}
    </span>
  );
}

export function DaysOffChoices({ calc, onChoose, fmtDate }) {
  const days = (calc.days || []).filter(d => d.off > 0);
  if (!days.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
      {days.map(d => (
        <div key={d.date} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
          <span style={{ fontFamily: 'var(--mono)', color: 'var(--text2)' }}>{fmtDate(d.date)}{d.off === 0.5 ? ' · half day' : ''}{d.leave ? ' · leave' : ''}</span>
          <PayWithToggle value={d.payWith} disabled={calc.paid} onChange={v => onChoose(d.date, v)} />
        </div>
      ))}
      {calc.paid && <div style={{ fontSize: 11, color: 'var(--text3)' }}>This salary is recorded as paid, so these are locked.</div>}
    </div>
  );
}

